import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, AlertTriangle, Users, Server, ArrowRight, CheckCircle } from 'lucide-react';
import { useSEO } from '../../hooks/useSEO';
import { Breadcrumb } from '../../components/Breadcrumb';
import { ServiceSchema } from '../../components/SchemaMarkup';
import { FAQ } from '../../components/FAQ';
import { useBanner } from '../../contexts/BannerContext';

export const CognitiveSecurePage: React.FC = () => {
  useSEO('cognitive-secure');
  const { isBannerVisible, bannerHeight } = useBanner();

  const features = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Advanced Threat Protection",
      description: "AI-powered threat detection and prevention systems that identify and neutralize cyber threats before they impact your business."
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Zero Trust Architecture",
      description: "Implement comprehensive zero-trust security models that verify every user and device before granting access to resources."
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "24/7 Security Monitoring",
      description: "Continuous monitoring and real-time threat intelligence with immediate response to security incidents."
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: "Vulnerability Assessment",
      description: "Regular security audits and penetration testing to identify and remediate potential vulnerabilities."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Security Awareness Training",
      description: "Comprehensive cybersecurity training programs to educate your team on the latest threats and best practices."
    },
    {
      icon: <Server className="w-6 h-6" />,
      title: "Infrastructure Security",
      description: "Complete security solutions for cloud, on-premises, and hybrid infrastructure environments."
    }
  ];

  const benefits = [
    "Significantly reduce cyber attack risk",
    "Ensure compliance with industry regulations",
    "Protect sensitive customer and business data",
    "Minimize downtime from security incidents",
    "Build customer trust through robust security",
    "Scale security measures with business growth"
  ];

  const processes = [
    {
      step: 1,
      title: "Security Assessment",
      description: "Comprehensive evaluation of your current security posture, identifying vulnerabilities and risk areas across all systems."
    },
    {
      step: 2,
      title: "Strategic Planning",
      description: "Develop a customized cybersecurity strategy aligned with your business objectives and compliance requirements."
    },
    {
      step: 3,
      title: "Implementation & Deployment",
      description: "Deploy advanced security solutions including firewalls, intrusion detection, encryption, and monitoring systems."
    },
    {
      step: 4,
      title: "Monitoring & Maintenance",
      description: "Continuous monitoring, threat hunting, and regular security updates to maintain optimal protection levels."
    }
  ];

  const faqItems = [
    {
      question: "What types of cyber threats can Cognitive Secure protect against?",
      answer: "Our comprehensive security suite protects against malware, ransomware, phishing attacks, DDoS attacks, insider threats, zero-day exploits, and advanced persistent threats (APTs). We use AI-powered detection to identify both known and unknown threats."
    },
    {
      question: "How quickly can you implement security solutions for my business?",
      answer: "Implementation timelines vary based on your organization's size and complexity. Basic security measures can be deployed within 1-2 weeks, while comprehensive enterprise solutions typically take 4-6 weeks for full deployment."
    },
    {
      question: "Do you provide compliance assistance for regulations like GDPR, HIPAA, or SOC 2?",
      answer: "Yes, we specialize in helping businesses achieve and maintain compliance with major regulations including GDPR, HIPAA, SOC 2, PCI DSS, and industry-specific standards. Our team stays updated on changing requirements."
    },
    {
      question: "What is included in your 24/7 security monitoring service?",
      answer: "Our Security Operations Center (SOC) provides continuous monitoring, threat detection, incident response, forensic analysis, and detailed reporting. We also offer immediate escalation procedures and emergency response protocols."
    },
    {
      question: "Can you secure both cloud and on-premises infrastructure?",
      answer: "Absolutely. We provide comprehensive security solutions for cloud environments (AWS, Azure, Google Cloud), on-premises systems, and hybrid infrastructures. Our approach ensures consistent security policies across all environments."
    },
    {
      question: "What is your incident response process if a security breach occurs?",
      answer: "We follow a structured incident response plan: immediate containment, thorough investigation, evidence preservation, system recovery, and detailed post-incident analysis. Our goal is to minimize impact and prevent future incidents."
    }
  ];

  return (
    <>
      <ServiceSchema
        serviceName="Cognitive Secure"
        description="Comprehensive cybersecurity solutions powered by AI and advanced threat intelligence to protect your business from evolving cyber threats."
        serviceType="Cybersecurity Services"
        url="https://cognitivecrafthub.com/services/cognitive-secure"
        features={[
          "Advanced Threat Protection",
          "Zero Trust Architecture", 
          "24/7 Security Monitoring",
          "Vulnerability Assessment",
          "Security Awareness Training",
          "Infrastructure Security"
        ]}
      />
      <Breadcrumb />
      <main className="pb-20">
        {/* Hero Section */}
      <section 
        className="px-4 sm:px-6 lg:px-8 mb-20"
        style={{ 
          paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px' // 64px header + 44px breadcrumb + 16px extra space
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl mb-6">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 
              className="text-5xl md:text-6xl font-bold mb-8 py-4 px-2 leading-loose bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent overflow-visible min-h-fit"
              style={{ lineHeight: '1.8', minHeight: 'auto', overflow: 'visible', paddingTop: '16px', paddingBottom: '16px' }}
            >
              Cognitive Secure
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
              AI-Powered Cybersecurity Solutions
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Protect your business with advanced cybersecurity solutions powered by artificial intelligence. 
              From threat detection to compliance management, we provide comprehensive security that evolves with the threat landscape.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Comprehensive Security Features</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Our cybersecurity platform combines cutting-edge AI technology with proven security methodologies
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
                <div className="text-blue-600 dark:text-blue-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{feature.title}</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Why Choose Cognitive Secure?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Our Security Process</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              A proven methodology to secure and protect your business assets
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {processes.map((process, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {process.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">{process.title}</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <div id="faq">
        <FAQ 
          items={faqItems}
          serviceName="Cognitive Secure"
        />
      </div>

      {/* CTA Section */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/5 dark:bg-gradient-to-r dark:from-blue-500/10 dark:to-blue-400/10 rounded-3xl p-12 border border-gray-200 dark:border-white/10">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Ready to Secure Your Business?
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
              Let's discuss how Cognitive Secure can protect your business from cyber threats 
              and ensure your digital assets remain safe and compliant.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
              >
                Get Security Assessment
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <button
                onClick={() =>
                  window.open(
                    "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                    "_blank"
                  )
                }
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                Schedule Consultation
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
    </>
  );
};